import { useMemo, useEffect, useCallback, useRef } from 'react'
import { Layout, Row, Col } from 'antd'
import GridLayout from 'react-grid-layout'
import { WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import Articles from './Components/Articles'
import ArticleTableFilters from '../../Components/Table/TableComponents/ArticleTableFilters'
import EditColumnsModal from '../../Components/Table/TableComponents/EditColumnsModal'
import ImagesModal from '../../Components/Kiosk/ImagesGallery'
import UpperMenu from './Components/UpperMenu'
import StickyArticleSelection from '../../Components/Kiosk/StickyArticleSelection'
import { setGridColumn, setStoredGridLayout } from '../../Slices/Kiosk/GridLayoutSlice'
import {
  barcodesSectionId,
  defaultGridRowHeight,
  descriptionsSectionId,
  imagesSectionId,
  marketplaceSectionId,
  movementSectionId,
  priceGenerationSectionId,
  pubblcationsSectionId,
  quantityLimitSectionId,
  quantitySectionId,
} from '../../Constants/Configuration'
import { addAndRemoveGridSection, changeGridColumns, instantiateResizeObserver } from '../../Helpers/GridLayoutHelper'
import QuantitiesGridLayout from './Components/QuantitiesGridLayout'
import MovementsAndAvailabilitiesGridLayout from './Components/MovementAndAvailabilitiesGridLayout'
import MarketplaceAvailabilitiesGridLayout from './Components/MarketplaceAvailabilitiesGridLayout'
import PricesGridLayout from './Components/PricesGridLayout'
import QauntityLimitGridLayout from './Components/QuantityLimitGridLayout'
import GridLayoutButtons from './Components/GridLayoutButtons'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { useAppDispatch, useAppSelector } from '../../hooks'
import ImagesGridLayout from './Components/ImagesGridLayout'
import { getTokenFromSessionStorage } from '../../Helpers/TokenHelper'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { getInfoByChannelType } from 'Helpers/GenericHelpers'
import DescriptionGridLayout from './Components/DescriptionsGridLayout'
import PubblicationGridLayout from './Components/PubblicationGridLayout'
import BarcodesGridLayout from './Barcodes/BarcodesGridLayout'

const { Content } = Layout
const ReactGridLayout = WidthProvider(GridLayout)

function Kiosk() {
  const dispatch = useAppDispatch()
  const showDrawer = useAppSelector(state => state.present.tableOptions.showDrawer)
  const gridLayout = useAppSelector(state => state.present.dynamicGrid.gridLayout)
  const gridOnOneColumn = useAppSelector(state => state.present.dynamicGrid.gridOnOneColumn)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.price.channelType)

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)
  const gridCointainerRef = useRef(null)
  const token = getTokenFromSessionStorage()

  useEffect(() => {
    getInfoByChannelType(typeOfChannel, dispatch)
  }, [dispatch, typeOfChannel])

  const domElementsRefObserver = useMemo(() => {
    return instantiateResizeObserver(dispatch)
  }, [dispatch])

  useEffect(() => {
    return () => {
      domElementsRefObserver && domElementsRefObserver.disconnect()
    }
  }, [])

  const updateGridLayout = useCallback(
    layoutId => {
      dispatch(setStoredGridLayout(addAndRemoveGridSection(gridLayout, layoutId, gridOnOneColumn)))
    },
    [dispatch, gridLayout, gridOnOneColumn],
  )

  useEffect(() => {
    if (articleKey && token) {
      dispatch(setGridColumn(true))
      dispatch(setStoredGridLayout(changeGridColumns(gridLayout, true)))
    }
  }, [articleKey, token])

  const gridChildren = useMemo(() => {
    return gridLayout
      .map(datagrid => {
        if (datagrid.i === quantitySectionId) {
          return (
            <QuantitiesGridLayout
              key={quantitySectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
            />
          )
        } else if (datagrid.i === movementSectionId) {
          return (
            <MovementsAndAvailabilitiesGridLayout
              key={movementSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
            />
          )
        } else if (datagrid.i === marketplaceSectionId) {
          return (
            <MarketplaceAvailabilitiesGridLayout
              key={marketplaceSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
            />
          )
        } else if (datagrid.i === priceGenerationSectionId) {
          return (
            <PricesGridLayout
              key={priceGenerationSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
            />
          )
        } else if (datagrid.i === quantityLimitSectionId) {
          return (
            <QauntityLimitGridLayout
              key={quantityLimitSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
              gridCointainerRef={gridCointainerRef}
            />
          )
        } else if (datagrid.i === imagesSectionId) {
          return (
            <ImagesGridLayout
              key={imagesSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
              gridCointainerRef={gridCointainerRef}
            />
          )
        } else if (datagrid.i === descriptionsSectionId) {
          return (
            <DescriptionGridLayout
              key={descriptionsSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
              gridCointainerRef={gridCointainerRef}
            />
          )
        } else if (datagrid.i === pubblcationsSectionId) {
          return (
            <PubblicationGridLayout
              key={pubblcationsSectionId}
              datagrid={datagrid}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
              gridCointainerRef={gridCointainerRef}
            />
          )
        } else if (datagrid.i === barcodesSectionId) {
          return (
            <BarcodesGridLayout
              key={barcodesSectionId}
              gridLayout={gridLayout}
              domElementsRefObserver={domElementsRefObserver}
              gridCointainerRef={gridCointainerRef}
            />
          )
        } else {
          return null
        }
      })
      .filter(x => x)
  }, [domElementsRefObserver, gridLayout])

  return (
    <Content className='page-content diwa' id='page-content-diwa' style={showDrawer ? { overflow: 'hidden' } : {}}>
      <StickyArticleSelection />
      <ArticleTableFilters />
      <Row>
        <Col span={24} className='table-content'>
          <UpperMenu isGridUpperMenu={false} />
          <Row className='article-table-content'>
            <Col span={24}>
              <Articles />
            </Col>
            <ImagesModal />
            <EditColumnsModal />
          </Row>
          <Row
            className='quantities-movements-availabilities-marketplace-content'
            id='multi-table-container'
            ref={gridCointainerRef}
          >
            <GridLayoutButtons
              gridLayout={gridLayout}
              gridOnOneColumn={gridOnOneColumn}
              updateGridLayout={updateGridLayout}
            />
            <Col span={24}>
              <ReactGridLayout
                className='grid-layout layout'
                layout={gridLayout}
                cols={12}
                autoSize={true}
                compactType='vertical'
                isBounded={true}
                allowOverlap={false}
                verticalCompact={true}
                rowHeight={defaultGridRowHeight}
                margin={[10, 0]}
                containerPadding={[0, 0]}
                draggableHandle={'.drag-handle-grid-container'}
                isResizable={false} // resize temporary disable
                onDragStop={layout => {
                  setTimeout(() => {
                    if (gridOnOneColumn) {
                      dispatch(setStoredGridLayout(layout.sort((a, b) => a.y - b.y)))
                    } else {
                      const leftGridElement = layout.filter(grid => grid.x < 6).sort((a, b) => a.y - b.y)
                      const rightGridElement = layout.filter(grid => grid.x === 6).sort((a, b) => a.y - b.y)

                      const sortedGridByHorizontalValue = []
                      for (let i = 0; i < Math.max(leftGridElement.length, rightGridElement.length); i++) {
                        if (leftGridElement[i]) sortedGridByHorizontalValue.push(leftGridElement[i])
                        if (rightGridElement[i]) sortedGridByHorizontalValue.push(rightGridElement[i])
                      }
                      dispatch(setStoredGridLayout(sortedGridByHorizontalValue))
                    }
                  }, 50)
                }}
                //resize temporary disable
                // onResizeStop = {(layout, oldItem, newItem) => {
                //     quantityBoxDomElementRef && quantityBoxDomElementRef.current && quantityBoxDomElementObserver && quantityBoxDomElementObserver.unobserve(quantityBoxDomElementRef.current);
                //     movementBoxDomElementRef && movementBoxDomElementRef.current && movementBoxDomElementRefObserver && movementBoxDomElementRefObserver.unobserve(movementBoxDomElementRef.current)
                //     marketBoxDomElementRef && marketBoxDomElementRef.current && movementBoxDomElementRefObserver && marketBoxDomElementRefObserver.unobserve(marketBoxDomElementRef.current);
                //     priceBoxDomElementRef && priceBoxDomElementRef.current && priceBoxDomElementRefObserver && priceBoxDomElementRefObserver.unobserve(priceBoxDomElementRef.current);

                //     if(newItem.i === quantitySectionId) {
                //         if(quantityBoxDomElementRef && quantityBoxDomElementRef.current) {
                //             quantityBoxDomElementRef.current.setAttribute("data-resized", true);
                //         }
                //     } else if(newItem.i === movementSectionId) {
                //         if(movementBoxDomElementRef && movementBoxDomElementRef.current) {
                //             movementBoxDomElementRef.current.setAttribute("data-resized", true);
                //         }
                //     } else if(newItem.i === marketplaceSectionId) {
                //         if(marketBoxDomElementRef && marketBoxDomElementRef.current) {
                //             marketBoxDomElementRef.current.setAttribute("data-resized", true);
                //         }
                //     } else {
                //         if(newItem.i === priceGenerationSectionId) {
                //             if(priceBoxDomElementRef && priceBoxDomElementRef.current) {
                //                 priceBoxDomElementRef.current.setAttribute("data-resized", true);
                //             }
                //         }
                //     }

                //     dispatch(setGridLayout(layout));
                // }}
              >
                {gridChildren}
              </ReactGridLayout>
            </Col>
          </Row>
        </Col>
      </Row>
    </Content>
  )
}

export default Kiosk
